// REACT IMPORTS
import { useMemo } from "react";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// COMPONENT IMPORTS
import { useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import {
  acceptQuotes,
  cancelSubscribePlan,
  chargeCustomer,
  checkAccountActiveSubscription,
  declineQuotes,
  dontCancelSubscribePlan,
  getDueAmount,
  getInoviceDetails,
  getPendingSubscriptionDetails,
  getProducts,
  getQuotesDetails,
  getSubscriptionCount,
  getSubscriptionHistory,
  makeInvoicePayment,
  makeInvoiceUpdate,
  sendEmailAcceptQuotes,
  sendEmailDeclineQuotes,
  sendInvoiceEmail,
  updatePendingSubscription,
  updateSubscribePlan,
  updateSubsPaymentMethod,
} from "store/services/subscriptionServices";

interface InitialState {
  subscriptionCount: any;
  subscriptionHistory: any;
  planDetails: any;
  isChecking: any;
  isSubscriptionAllowed: any;
}

const initialState: InitialState = {
  subscriptionCount: {},
  subscriptionHistory: {},
  planDetails: {},
  isChecking: false,
  isSubscriptionAllowed: true,
};

export const subscriptionCountGet = createAsyncThunk(
  "subscription-count/get",
  async (params: any) => {
    return await getSubscriptionCount(params);
  }
);

export const subscriptionHistoryGet = createAsyncThunk(
  "subscription-history/get",
  async (payload: any) => {
    return await getSubscriptionHistory(payload);
  }
);

const productsListGet = createAsyncThunk(
  "products-list/get",
  async (params: any) => {
    return await getProducts(params);
  }
);

export const checkAccountSubscription = createAsyncThunk(
  "check-account-subscription/post",
  async (payload: any) => {
    return await checkAccountActiveSubscription(payload);
  }
);

export const productsDueAmount = createAsyncThunk(
  "products/due-amount",
  async (payload: any) => {
    return await getDueAmount(payload);
  }
);

export const updatePlan = createAsyncThunk(
  "update-plan/subscribe",
  async (payload: any) => {
    return await updateSubscribePlan(payload);
  }
);

export const cancelPlan = createAsyncThunk(
  "cancel-plan/subscribe",
  async (payload: any) => {
    return await cancelSubscribePlan(payload);
  }
);

export const dontCancelPlan = createAsyncThunk(
  "dont-cancel-plan/subscribe",
  async (payload: any) => {
    return await dontCancelSubscribePlan(payload);
  }
);

export const updateSubscriptionPaymentMethod = createAsyncThunk(
  "update-subscription-payment-method/post",
  async (payload: any) => {
    return await updateSubsPaymentMethod(payload);
  }
);

export const pendingSubscriptionDetailsGet = createAsyncThunk(
  "pending-subscription-details/get",
  async (params: any) => {
    return await getPendingSubscriptionDetails(params);
  }
);

export const pendingSubscriptionUpdate = createAsyncThunk(
  "pending-subscription-details/update",
  async (params: any) => {
    return await updatePendingSubscription(params);
  }
);

export const invoiceDetailsGet = createAsyncThunk(
  "invoice-details/get",
  async (params: any) => {
    return await getInoviceDetails(params);
  }
);

export const invoiceDetailsSend = createAsyncThunk(
  "invoice-details/send",
  async (payload: any) => {
    return await sendInvoiceEmail(payload);
  }
);

export const invoiceUpdate = createAsyncThunk(
  "invoice-update/post",
  async (payload: any) => {
    return await makeInvoiceUpdate(payload);
  }
);

export const invoicePayment = createAsyncThunk(
  "invoice-payment/post",
  async (payload: any) => {
    return await makeInvoicePayment(payload);
  }
);

export const quotesDetailsGet = createAsyncThunk(
  "quotes-details/get",
  async (params: any) => {
    return await getQuotesDetails(params);
  }
);

export const quotesDetailsAccept = createAsyncThunk(
  "quotes-details/accept",
  async (params: any) => {
    return await acceptQuotes(params);
  }
);

export const quotesDetailsDecline = createAsyncThunk(
  "quotes-details/decline",
  async (params: any) => {
    return await declineQuotes(params);
  }
);

export const chargeCustomerOnSubscribe = createAsyncThunk(
  "charge-customer/post",
  async (payload: any) => {
    return await chargeCustomer(payload);
  }
);

export const emailDeclineQuotes = createAsyncThunk(
  "email-quotes-details/decline",
  async (payload: any) => {
    return await sendEmailDeclineQuotes(payload);
  }
);

export const emailAcceptQuotes = createAsyncThunk(
  "email-quotes-details/accept",
  async (payload: any) => {
    return await sendEmailAcceptQuotes(payload);
  }
);

const prepareData = (data: any) => {
  // let nData = data?.paymentStatusCounts;
  // let total = 0;
  // for (const key in nData) {
  //   if (nData.hasOwnProperty(key)) {
  //     total += nData[key];
  //   }
  // }
  let fData = { ...data?.paymentStatusCounts };
  return { ...fData };
};

/* ------------------------------------- SUBSCRIPTION SLICE ------------------------------------- */

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    clearSubscriptionSlice: (state) => {
      for (const key in initialState) {
        if (initialState.hasOwnProperty(key)) {
          const typedKey = key as keyof typeof initialState;
          state[typedKey] = initialState[typedKey];
        }
      }
    },
    storePlanDetails: (state, action) => {
      state.planDetails = action.payload;
    },
    resetSubscriptionState: (state) => {
      state.isChecking = false;
      state.isSubscriptionAllowed = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      subscriptionCountGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.subscriptionCount = prepareData(action.payload?.body?.data);
      }
    );

    builder.addCase(
      subscriptionHistoryGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.subscriptionHistory = action.payload?.body?.data || {};
      }
    );

    builder.addCase(
      checkAccountSubscription.pending,
      (state, action: PayloadAction<any>) => {
        state.isChecking = true;
      }
    );

    builder.addCase(
      checkAccountSubscription.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.isChecking = false;
        state.isSubscriptionAllowed =
          action.payload?.body?.data?.allowed || false;
      }
    );

    builder.addCase(
      checkAccountSubscription.rejected,
      (state, action: PayloadAction<any>) => {
        state.isChecking = false;
        state.isSubscriptionAllowed = true;
      }
    );
  },
});

export const {
  clearSubscriptionSlice,
  storePlanDetails,
  resetSubscriptionState,
} = subscriptionSlice.actions;
export default subscriptionSlice.reducer;

const selectSubscriptionCount = (state: RootState) =>
  state.subscription.subscriptionCount;
export const useSubscriptionCount = () => {
  const subscriptionCount = useAppSelector(selectSubscriptionCount);
  return useMemo(() => subscriptionCount, [subscriptionCount]);
};

const selectSubscriptionHistory = (state: RootState) =>
  state.subscription.subscriptionHistory;
export const useSubscriptionHistory = () => {
  const subscriptionHistory = useAppSelector(selectSubscriptionHistory);
  return useMemo(() => subscriptionHistory, [subscriptionHistory]);
};
